import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight, Mail } from "lucide-react";
import { CONTACT } from "@/data/content";

const CAP_LINKS = [
  { label: "Overview", to: "/capabilities" },
  { label: "Infrastructure", to: "/capabilities/infrastructure" },
  { label: "Products", to: "/capabilities/products" },
];

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Capabilities", to: "/capabilities", dropdown: true },
  { label: "Industries & Work", to: "/industries" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCapOpen(false);
  }, [location.pathname]);

  const capActive = location.pathname.startsWith("/capabilities");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-border"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex items-center justify-between h-[88px]">
          {/* Wordmark */}
          <Link to="/" data-testid="logo-wordmark" className="group flex items-center gap-3">
            <img src="/assets/brand/logo.png" alt="Krystal Technology" className="h-16 md:h-[68px] w-auto transition-transform duration-300 group-hover:scale-105" />
            <span className="font-display font-extrabold tracking-tight text-[22px] md:text-[26px] leading-none uppercase">
              Krystal<span className="text-muted-foreground font-medium"> Technology</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) =>
              item.dropdown ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setCapOpen(true)}
                  onMouseLeave={() => setCapOpen(false)}
                >
                  <Link
                    to="/capabilities"
                    data-testid="nav-capabilities"
                    className={`flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium tracking-wide transition-colors ${
                      capActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Capabilities
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${capOpen ? "rotate-180" : ""}`} />
                  </Link>
                  <AnimatePresence>
                    {capOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full pt-2 w-[300px]"
                      >
                        <div className="bg-white border border-border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]">
                          {CAP_LINKS.map((c, i) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              data-testid={`nav-dropdown-${i}`}
                              className="flex items-center justify-between px-5 py-4 border-b border-border/60 last:border-0 group hover:bg-secondary transition-colors"
                            >
                              <span>
                                <span className="font-mono-tech text-[10px] text-[hsl(var(--signal))] mr-2">0{i + 1}</span>
                                <span className="text-[14px] font-medium">{c.label}</span>
                              </span>
                              <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase().split(" ")[0]}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-[15px] font-medium tracking-wide transition-colors ${
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="/brochure"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-brochure-button"
              className="hidden sm:inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] font-medium tracking-wide uppercase hover:bg-[hsl(var(--signal))] transition-colors duration-300"
            >
              View Brochure
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <a
              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${CONTACT.email}&su=Request for Quotation`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rfq-button"
              aria-label="Request for Quotation"
              className="hidden sm:inline-flex items-center gap-2 border border-foreground text-foreground px-4 py-2.5 text-[12px] font-medium tracking-wide uppercase hover:bg-foreground hover:text-background transition-colors duration-300"
            >
              <Mail className="h-3.5 w-3.5" />
              RFQ
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 -mr-2"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-b border-border overflow-hidden"
          >
            <nav className="px-6 py-4 flex flex-col">
              {NAV.filter((n) => !n.dropdown).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className="py-3 text-[15px] font-medium border-b border-border/60"
                  data-testid={`mobile-nav-${item.label.toLowerCase().split(" ")[0]}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="py-2">
                <NavLink
                  to="/capabilities"
                  data-testid="mobile-nav-capabilities"
                  className="block py-3 text-[15px] font-medium border-b border-border/60"
                >
                  Capabilities
                </NavLink>
                {CAP_LINKS.map((c) => (
                  <NavLink key={c.to} to={c.to} className="block py-2.5 pl-4 text-[14px] text-muted-foreground">
                    {c.label}
                  </NavLink>
                ))}
              </div>
              <a
                href="/brochure"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-foreground text-background px-5 py-3 text-[12px] font-medium tracking-wide uppercase"
              >
                View Brochure <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
